// Regroupement des capacités par thème, pour les écrans qui font choisir
// dans la banque.
//
// La banque est plate ; le référentiel officiel, non. Chaque capacité porte
// un thème — sept en seconde, cinq en première spé — et c'est la seule
// arborescence dont on dispose sans en inventer une seconde à entretenir
// (cf. CLAUDE.md §8 : le lien vers le référentiel n'existe qu'à un endroit).

import { api, type Capacite } from "./api";

/** Capacités de plusieurs niveaux, indexées par code, dans l'ordre du programme. */
export async function chargerReferentiel(
  niveaux: string[],
): Promise<Map<string, Capacite>> {
  const listes = await Promise.all(
    // Un niveau absent ne doit pas emporter les autres : l'écran reste
    // utilisable sans son filtre.
    niveaux.map((niveau) => api.capacitesDuNiveau(niveau).catch(() => [])),
  );
  return new Map(listes.flat().map((capacite) => [capacite.code, capacite]));
}

export type GroupeTheme = {
  theme: string;
  capacites: { code: string; libelle: string; nombre: number }[];
};

export const SANS_CAPACITE = "aucune";
export const TOUTES_CAPACITES = "toutes";

/** Les capacités effectivement portées par `questions`, groupées par thème.
 *
 * Seules celles qui portent au moins une question figurent : la liste suit la
 * banque, pas le programme entier — 109 capacités pour la seule seconde, dont
 * la plupart n'auraient aucune question à offrir.
 *
 * Les thèmes sortent dans l'ordre du programme, pas dans l'ordre
 * alphabétique : c'est celui dans lequel le professeur les enseigne.
 */
export function grouperParTheme(
  questions: { capacites: string[] }[],
  referentiel: Map<string, Capacite>,
): GroupeTheme[] {
  const comptes = new Map<string, number>();
  for (const question of questions) {
    for (const code of question.capacites) {
      comptes.set(code, (comptes.get(code) ?? 0) + 1);
    }
  }

  const groupes: GroupeTheme[] = [];
  const ajouter = (theme: string, capacite: GroupeTheme["capacites"][0]) => {
    let groupe = groupes.find((g) => g.theme === theme);
    if (!groupe) groupes.push((groupe = { theme, capacites: [] }));
    groupe.capacites.push(capacite);
  };

  for (const [code, capacite] of referentiel) {
    const nombre = comptes.get(code);
    if (nombre) ajouter(capacite.theme, { code, libelle: capacite.libelle, nombre });
  }

  // Un code que le référentiel chargé ne connaît pas — niveau non chargé,
  // programme remplacé — ne doit pas rendre ses questions introuvables.
  for (const [code, nombre] of comptes) {
    if (!referentiel.has(code)) ajouter("Hors référentiel", { code, libelle: code, nombre });
  }

  return groupes;
}

/** Un libellé de capacité tient rarement dans une option de menu. */
export function abreger(libelle: string, taille = 70): string {
  const propre = libelle.trim().replace(/\s*;\s*$/, "");
  return propre.length <= taille ? propre : `${propre.slice(0, taille - 1)}…`;
}
